import { ActionType, ProTable } from "@ant-design/pro-components";
import { Button, Form, message, Modal, Popconfirm } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useGlobalState } from "../../components/global";
import { useEffect, useRef, useState } from "react";
import GlobalWrap from "../../components/globalWrap";
import MenuForm from "./MenuForm";
import UUIDComponent from "../../components/UUIDComponent";
import * as AntdIcons from "@ant-design/icons";

export default function Menu() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [expandedAll, setExpandedAll] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [form] = Form.useForm();

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const columns: any[] = [
        {
            title: "菜单名称",
            dataIndex: "menuName",
            align: "center",
            search: false,
            width: 300,
            render: (_: any, item: any) => {
                // 获取图标组件
                const IconComponent = item.type === 'antd' && item.icon ?
                    (AntdIcons as any)[item.icon] : null;

                return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {item.type === 'antd' && IconComponent ? (
                        // 渲染 Ant Design 图标组件
                        <IconComponent style={{ fontSize: '20px' }} />
                    ) : item.url ? (
                        // 渲染图片图标
                        <img src={item.url} style={{
                            width: 20,
                            height: 20,
                            objectFit: "cover",
                            verticalAlign: 'middle'
                        }} />
                    ) : null}
                    <span>{item.menuName}</span>
                </div>
            }
        },
        {
            title: "ID",
            dataIndex: "id",
            align: "center",
            search: false,
            render: (value: string) => <UUIDComponent value={value} />,
            width: 100,
        },
        {
            title: "排序",
            dataIndex: "sortNum",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "路由地址",
            dataIndex: "path",
            align: "center",
            search: false,
            width: 200,
        },
        {
            title: "组件路径",
            dataIndex: "component",
            align: "center",
            search: false,
            width: 200,
        },
        {
            title: "路由参数",
            dataIndex: "query",
            align: "center",
            search: false,
            width: 200,
        },
        {
            title: "路由名称",
            dataIndex: "routeName",
            align: "center",
            search: false,
            width: 200,
        },
        {
            title: "是否隐藏",
            dataIndex: "hiddenFlag",
            align: "center",
            search: false,
            width: 200,
            render: (value: boolean) => value ? "是" : "否",
        },
        {
            title: "备注",
            dataIndex: "remark",
            align: "center",
            search: false,
            width: 200,
        },
        {
            title: "创建时间",
            dataIndex: "createAt",
            search: false,
            align: "center",
            width: 200,
        },
        {
            title: "操作",
            key: 'operation',
            search: false,
            align: "center",
            width: 150,
            fixed: "right",
            render: (_: any, record: any) => (
                <>
                    <Button type='link' size='small' onClick={() => handleUpdateOrInsert(record)}>
                        编辑
                    </Button>
                    <Popconfirm title="确定删除？" okText="是" cancelText="否"
                        onConfirm={() => deleteHandle(record.id)}>
                        <Button type='link' size='small' danger>删除</Button>
                    </Popconfirm>
                </>
            ),
        }
    ];

    const deleteHandle = async (id: any) => {
        const result: any = await jsonRequest.delete("/back/sys-menu/delete", {
            ids: [id]
        });
        if (result?.body.code === 0)
            message.success('删除成功');
        else {
            message.error('删除失败');
            console.log(result);
        }
        tableRef.current?.reload();
    }

    const handleUpdateOrInsert = (item: any) => {
        Modal.confirm({
            width: 500,
            title: !!item.id ? `菜单编辑 - ${item.menuName}` : "菜单新增",
            content: <GlobalWrap><MenuForm form={form} item={item} menuList={dataSource} /></GlobalWrap>,
            onOk: async (close) => {
                let values = await form.validateFields();
                if (!!item?.id) {
                    await jsonRequest.put("/back/sys-menu/modify", {
                        ...values,
                        id: item.id,
                    }).then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("编辑成功");
                            } else {
                                message.error(data?.data?.msg || "编辑失败");
                            }
                        })
                } else {
                    await jsonRequest.post("/back/sys-menu/add", { ...values })
                        .then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("新增成功");
                            } else {
                                message.error(data?.data?.msg || "新增失败");
                            }
                        })
                }
                tableRef.current?.reload();
                form.resetFields();
                close();
                return false;
            },
            onCancel: (close) => {
                form.resetFields();
                close();
            }
        })
    }

    const init = async (url: any) => {
        const result = await jsonRequest.get(`/common/oss-file-visit-url`, { fileUrl: url })
            .then((resp: any) => resp.json());
        return result?.data;
    };

    const processTreeData = async (data: any[]): Promise<any[]> => {
        return await Promise.all(data.map(async (item: any) => {
            let processedItem = { ...item };

            // 处理当前节点的 icon
            if (item.icon) {
                if (item.icon.startsWith('http') || item.icon.includes('oss')) {
                    // 处理图片地址
                    const url = await init(item.icon);
                    processedItem = { ...processedItem, url: url };
                } else {
                    // 处理 Ant Design 图标名称
                    processedItem = { ...processedItem, type: 'antd' };
                }
            }

            // 递归处理子节点
            if (item.children && item.children.length > 0) {
                processedItem.children = await processTreeData(item.children);
            }

            return processedItem;
        }));
    };

    const request = async (params: any) => {
        const result = await jsonRequest.get("/back/sys-menu/tree", {
            ...params
        }).then((resp: any) => resp.json());
        const { data } = result;
        setDataSource(data);
        const processedData = await processTreeData(data || []);
        return { data: processedData, success: result.code === 0 };
    };

    const getAllIds = (data: any[]): string[] => {
        return data.reduce((acc: string[], item: any) => {
            acc.push(item.id);
            if (item.children && item.children.length > 0) {
                acc.push(...getAllIds(item.children));
            }
            return acc;
        }, []);
    };

    return <>
        <h1>菜单管理</h1>
        <ProTable
            rowKey="id"
            actionRef={tableRef}
            scroll={{ x: 1500 }}
            cardBordered
            bordered
            columns={columns}
            request={request}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            search={{ span: 6, defaultCollapsed: false }}
            expandable={{
                childrenColumnName: 'children',
                defaultExpandAllRows: false,
                expandedRowKeys: expandedAll ? getAllIds(dataSource) : expandedRowKeys,
                onExpand: (expanded, record) => {
                    if (expandedAll && !expanded) {
                        // 如果在全部展开模式下手动折叠某项
                        const newExpandedKeys = expandedRowKeys.filter(key => key !== record.id);
                        setExpandedRowKeys(newExpandedKeys);
                        setExpandedAll(false);
                    } else if (!expandedAll) {
                        // 正常模式下手动展开/折叠
                        setExpandedRowKeys(prevKeys => {
                            return expanded
                                ? [...prevKeys, record.id]
                                : prevKeys.filter(key => key !== record.id);
                        });
                    }
                }
            }}
            toolBarRender={() => [<>
                <Button
                    key="button"
                    icon={<PlusOutlined />}
                    onClick={() => handleUpdateOrInsert({})}
                    type="primary"
                >
                    新增
                </Button>
                <Button
                    key="expand"
                    onClick={() => {
                        if (expandedAll) {
                            setExpandedRowKeys([]);
                        } else {
                            setExpandedRowKeys(getAllIds(dataSource));
                        }
                        setExpandedAll(!expandedAll);
                    }}
                    type="primary"
                >
                    {expandedAll ? '全部折叠' : '全部展开'}
                </Button>
            </>]}
        />
    </>
}